import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';
import baseFetchWithAuth from '../../utils/fetchWithAuth';
import InfoTip from '../ui/InfoTip';
import SortableTable from '../SortableTable';

// ── Design tokens (aligned with the GPS "Général" tab) ──
const ACCENT = '#5b3df0';
const SERIES_COLORS = ['#5b3df0', '#2f6df0', '#f5a623', '#16a866', '#36a7d6', '#7c5cff', '#e5484d', '#2bbfae', '#9b8cf5', '#f0a35e'];
// Status semantics keep a stable meaning-based color wherever they appear.
const STATUS_COLORS = { DONE: '#16a866', INDETERMINATE: '#f5a623', NEW: '#2f6df0' };
const colorFor = (key, i) => STATUS_COLORS[key] || SERIES_COLORS[i % SERIES_COLORS.length];

const TOOLTIP_STYLE = { backgroundColor: 'white', border: '1px solid #e7e9ef', borderRadius: '10px', boxShadow: '0 4px 12px rgba(20,25,40,.08)', fontSize: 12 };

const nf = (v) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : v);

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const formatMonthFr = (v) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(v));
  return m ? MONTHS_FR[+m[2] - 1] : v;
};

// A missing value is a fact about the data, not something to hide. One neutral
// placeholder everywhere — the same wording the pivot writes server-side, so a
// legend and a table cell never disagree about the same empty cell.
const EMPTY_LABEL = '(non renseigné)';
const sanitizeLabel = (v) => (v == null || v === 'null' || v === '') ? EMPTY_LABEL : v;
function sanitizeData(data) {
  if (!Array.isArray(data)) return data;
  return data.map(row => {
    const sanitized = {};
    Object.entries(row).forEach(([key, value]) => {
      sanitized[sanitizeLabel(key)] = typeof value === 'string' ? sanitizeLabel(value) : value;
    });
    return sanitized;
  });
}

// ── Icons (lucide-style, 18px, currentColor) ──
const svg = (children) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
const ICONS = {
  bar: svg(<><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>),
  pie: svg(<><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></>),
  list: svg(<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>),
  table: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="12" y1="3" x2="12" y2="21" /></>),
  kpi: svg(<><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>),
};

// The design's KPI group glyph: three filled bars, not a line chart.
const KPI_GROUP_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="15" height="15" aria-hidden>
    <rect x="1" y="8" width="3.4" height="7" rx="1" fill="currentColor" />
    <rect x="6.3" y="4" width="3.4" height="11" rx="1" fill="currentColor" />
    <rect x="11.6" y="1" width="3.4" height="14" rx="1" fill="currentColor" />
  </svg>
);

// Group surface + icon chip, per variant. 'accent' is the indigo-tinted headline
// group; 'warn' is the white group with an amber chip.
const KPI_VARIANTS = {
  accent: {
    group: 'bg-[#f7f5fe] border-[#e6e1fa] dark:bg-[#221e3a] dark:border-[#2d2947]',
    chip: 'bg-[#5b3df0] text-white',
  },
  warn: {
    group: 'bg-white border-gray-200 dark:bg-[#1e1e1e] dark:border-gray-900',
    chip: 'bg-[#fdf2e0] text-[#a06a10] dark:bg-[#33270f] dark:text-[#e3a94f]',
  },
  plain: {
    group: 'bg-white border-gray-200 dark:bg-[#1e1e1e] dark:border-gray-900',
    chip: 'bg-[#f3f1fe] text-[#5b3df0] dark:bg-[#2a2440] dark:text-[#a98ff5]',
  },
};

// Semantic value colours, keyed by measure.
// Flux vs encours — the client marks this herself on her bilan, where only the two
// current-status tiles carry "au 25/06". The nature of each measure comes from the
// server (block.measureNatures); the wording below is presentation.
const NATURE_META = {
  flux: {
    label: 'SUR LA PÉRIODE',
    badge: 'bg-[#f3f1fe] text-[#4930c4] dark:bg-[#2a2440] dark:text-[#a98ff5]',
    hint: "Additionne ce qui s'est passé pendant la période — une intervention faite tel jour, une demande formulée tel jour. Le chiffre est définitif : rouvrir la même période redonnera toujours la même valeur.",
  },
  stock_asof: {
    label: 'ÉTAT FIN DE PÉRIODE',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    hint: "Photo d'un état, prise à la date de fin de la période et reconstruite depuis l'historique : le compteur monte et descend (un blocage levé en sort, sans rien changer au passé). Le chiffre est rejouable : rouvrir la même période redonne la même photo.",
  },
  stock_now: {
    label: 'ÉTAT À CE JOUR',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    hint: "Photo d'un état, prise au dernier import de données : ce chiffre bouge dans le temps même quand la période ne change pas.",
  },
};

function NatureBadge({ nature, endDate }) {
  const meta = NATURE_META[nature];
  if (!meta) return null;
  const label = nature === 'stock_asof' && endDate
    ? `ÉTAT AU ${dayjs(endDate).format('DD/MM')}`
    : meta.label;

  return (
    <span
      title={meta.hint}
      className={`inline-block shrink-0 cursor-help rounded-md px-1.5 py-0.5 text-[9px] font-bold leading-none tracking-[0.06em] ${meta.badge}`}
    >
      {label}
    </span>
  );
}

// Spells out, once per block, which figures accumulate over the period and which
// read a current status — the question the reader cannot answer from the number.
function NatureLegend({ natures = {}, labels = {}, endDate }) {
  const present = Object.keys(NATURE_META).filter((nature) => Object.values(natures).includes(nature));
  // Un encours mérite toujours son explication — c'est lui que le lecteur prend
  // pour un cumul. Un bloc tout en flux n'a besoin de la légende que s'il y a
  // deux natures à distinguer.
  const hasStock = present.some((nature) => nature.startsWith('stock'));
  if (!hasStock && present.length < 2) return null;

  return (
    <dl className="mt-3.5 flex flex-col gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
      {present.map((nature) => (
        <div key={nature} className="flex items-baseline gap-2">
          <dt><NatureBadge nature={nature} endDate={endDate} /></dt>
          <dd className="m-0 min-w-0 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            <b className="font-semibold text-gray-600 dark:text-gray-300">
              {Object.keys(natures).filter((key) => natures[key] === nature).map((key) => labels[key] || key).join(', ')}
            </b>
            {' — '}{NATURE_META[nature].hint}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const KPI_TONES = {
  alert: 'text-[#d13c42] dark:text-[#f07a7f]',
  warn: 'text-[#c07f12] dark:text-[#e3a94f]',
};

const Empty = () => <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10">Aucune donnée</p>;

// A subtitle carries two different things: what the block counts, which must stay
// readable at a glance, and the method with its caveats, which matters just as much
// but is only read when in doubt. The card shows the former and puts the latter
// behind an info tip.
//
// Two modes. When the block declares `details`, the hand-written split is honoured.
// Otherwise an over-long subtitle is cut at its first sentence for display and
// poured whole into the tip: no block can drown its figures anymore, not even on a
// page nobody re-read.
const SUBTITLE_MAX = 160;

function splitSubtitle(subtitle, details) {
  if (!subtitle) return { visible: null, tip: details || null };
  if (details) return { visible: subtitle, tip: details };
  if (subtitle.length <= SUBTITLE_MAX) return { visible: subtitle, tip: null };

  // First sentence = up to the first period followed by whitespace and a capital.
  // Never cuts on "..." nor on a digit-glued period ("4.5"), and if that first
  // sentence still exceeds the limit everything is kept: long beats randomly cut.
  const cut = subtitle.search(/\.\s+\p{Lu}/u);
  if (cut < 0 || cut + 1 > SUBTITLE_MAX) return { visible: subtitle, tip: null };
  return { visible: subtitle.slice(0, cut + 1), tip: subtitle };
}

// The block's technical id, as it appears in the stored page and the audit
// dossier — the map between what the reader sees and what the config declares.
function SlugTag({ id }) {
  if (!id) return null;
  return (
    <span
      title="Identifiant technique du bloc — le même que dans la configuration et le dossier d'audit"
      className="ml-auto flex-none cursor-help rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] leading-none text-gray-400 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500"
    >
      {id}
    </span>
  );
}

// ── Card wrapper (icon chip + title + subtitle + right meta) ──
function BlockCard({ icon, title, subtitle, details, meta, slug, children }) {
  const { visible, tip } = splitSubtitle(subtitle, details);

  return (
    <div className="bg-white dark:bg-[#1e1e1e] p-5 sm:p-6 rounded-2xl border border-gray-200 dark:border-gray-900 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] flex-none">
          {icon}
        </span>
        <div className="min-w-0">
          <span className="flex items-center gap-1.5 text-base font-bold text-gray-900 dark:text-white">
            {title}
            {tip && <InfoTip text={tip} width="w-72" />}
          </span>
          {visible && <span className="block text-xs text-gray-500 dark:text-gray-400">{visible}</span>}
        </div>
        {meta && <div className="ml-auto text-xs font-semibold text-gray-400 dark:text-gray-500">{meta}</div>}
        <SlugTag id={slug} />
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function SeriesLegend({ series, labelOf = sanitizeLabel }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 justify-center">
      {series.map((k, i) => (
        <span key={k} className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
          <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: colorFor(k, i) }} />
          {labelOf(k)}
        </span>
      ))}
    </div>
  );
}

// ── KPI group ──
// Two groups sit side by side on the overview: the accented headline group and the
// urgences group. Card metrics come straight from the design — 12px label, 26px
// value, 11px caption — so this page does not reuse GPSKpiTile, whose tile carries
// a different rhythm (tooltip slot, comparison wording, fill variants).
function KpiGroupCard({ block, children }) {
  const variant = KPI_VARIANTS[block.encoding?.variant] || KPI_VARIANTS.plain;
  const { visible, tip } = splitSubtitle(block.subtitle, block.details);

  return (
    <section className={`h-full rounded-[14px] border p-[18px] ${variant.group}`}>
      <div className="mb-4 flex items-start gap-[11px]">
        <span className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-[9px] ${variant.chip}`}>
          {KPI_GROUP_ICON}
        </span>
        <div className="min-w-0">
          <h2 className="m-0 flex items-center gap-1.5 text-base font-semibold text-gray-900 dark:text-white">
            {block.title}
            {tip && <InfoTip text={tip} width="w-72" />}
          </h2>
          {visible && (
            <p className="m-0 mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{visible}</p>
          )}
        </div>
        <SlugTag id={block.id} />
      </div>
      {children}
    </section>
  );
}

function KpiCard({ label, value, tone, caption, nature, endDate }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-900 dark:bg-[#1e1e1e]">
      {/* Le badge occupe sa propre ligne : ses libellés datés (« ÉTAT AU 25/06 »)
          écrasaient le titre de la tuile quand ils partageaient la sienne. */}
      {nature && (
        <div className="flex justify-end">
          <NatureBadge nature={nature} endDate={endDate} />
        </div>
      )}
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-[26px] font-bold leading-none tracking-tight ${KPI_TONES[tone] || 'text-gray-900 dark:text-white'}`}>
        {nf(value)}
      </div>
      {caption && <div className="text-[11px] text-gray-400 dark:text-gray-500">{caption}</div>}
    </div>
  );
}

function KpiSection({ block, data, labels = {}, natures = {}, endDate }) {
  const items = data || [];
  // 'captions' is keyed by measure, like 'tones': two tiles of the same group may
  // not be dated by the same thing ("PDV réalisés" is dated by the intervention,
  // its neighbours by the request), and nothing used to say so.
  const { tones = {}, captions = {} } = block.encoding || {};

  return (
    <KpiGroupCard block={block}>
      {items.length === 0 ? <Empty /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((item) => {
            // SQL renders numbers (parfois en chaînes) ; une requête nommée peut
            // renvoyer un texte déjà formaté (« 34 % ») — il s'affiche tel quel.
            const numeric = Number(item.value);
            const value = Number.isFinite(numeric) && String(item.value).trim() !== '' ? numeric : item.value;
            return (
              <KpiCard
                key={item.label}
                label={labels[item.label] || item.label}
                value={value}
                tone={tones[item.label]}
                caption={captions[item.label]}
                nature={natures[item.label]}
                endDate={endDate}
              />
            );
          })}
        </div>
      )}
      <NatureLegend natures={natures} labels={labels} endDate={endDate} />
    </KpiGroupCard>
  );
}

// ── Donut with center % + legend rows ──
function DonutRenderer({ data, mapping }) {
  const rows = (data || [])
    .map(d => ({ name: sanitizeLabel(d[mapping.name]), value: Number(d[mapping.value]) || 0 }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value);
  if (!rows.length) return <Empty />;
  const total = rows.reduce((s, r) => s + r.value, 0);
  const top = rows[0];
  const topPct = total ? Math.round((top.value / total) * 100) : 0;

  return (
    <div className="flex items-center gap-5 sm:gap-7">
      <div className="relative flex-none" style={{ width: 168, height: 168 }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-extrabold leading-none text-gray-900 dark:text-white">{topPct}%</span>
          <span className="mt-1 text-[11px] font-semibold text-gray-400 dark:text-gray-500 max-w-[92px] truncate text-center">{top.name}</span>
        </div>
        <ResponsiveContainer width={168} height={168}>
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={82} innerRadius={58} paddingAngle={1.2} startAngle={90} endAngle={-270} label={false} stroke="none">
              {rows.map((r, i) => <Cell key={i} fill={colorFor(r.name, i)} />)}
            </Pie>
            <Tooltip wrapperStyle={{ zIndex: 30 }} contentStyle={TOOLTIP_STYLE} formatter={(value, name) => [`${nf(value)} (${((value / total) * 100).toFixed(1)}%)`, name]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-2.5 text-xs">
        {rows.map((item, i) => {
          const pct = ((item.value / total) * 100).toFixed(1).replace('.', ',');
          return (
            <span key={i} className="flex items-center gap-2.5 font-semibold text-gray-600 dark:text-gray-300">
              <span className="w-2.5 h-2.5 rounded-[3px] flex-none" style={{ background: colorFor(item.name, i) }} />
              <span className="truncate">{item.name}</span>
              <span className="ml-auto pl-2 text-gray-900 dark:text-white whitespace-nowrap">{nf(item.value)} · {pct} %</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Horizontal "meter" list, ranked by value or in a declared order ──
// `config.order` lists the labels in the order they must read — for a scale
// (a delay, an age), value-ranking jumbles the axis and the reader loses the
// progression. Labels absent from the list keep their rank after the named ones.
function MeterRenderer({ data, mapping, config = {} }) {
  const order = Array.isArray(config.order) ? config.order : null;
  const rank = (name) => {
    const i = order.indexOf(name);
    return i === -1 ? order.length : i;
  };
  const rows = (data || [])
    .map(d => ({ name: sanitizeLabel(d[mapping.name]), value: Number(d[mapping.value]) || 0 }))
    .filter(r => r.value > 0)
    .sort((a, b) => (order ? rank(a.name) - rank(b.name) || b.value - a.value : b.value - a.value));
  if (!rows.length) return <Empty />;
  // Not rows[0]: with a declared order the first row is not the longest bar.
  const max = Math.max(...rows.map(r => r.value));
  const color = config.color || ACCENT;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-28 sm:w-36 shrink-0 truncate text-xs font-semibold text-gray-600 dark:text-gray-300" title={r.name}>{r.name}</span>
          <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${max ? Math.round((r.value / max) * 100) : 0}%`, background: color }} />
          </div>
          <span className="w-10 shrink-0 text-right text-xs font-bold text-gray-900 dark:text-white tabular-nums">{nf(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Every key that is not the axis, scanned across ALL rows — not just the first.
// The declarative pivot fills its grid with zeros, but the escape-hatch queries build
// their rows in JS and omit absent pairs, so a series appearing only from the second
// row onwards used to vanish from the whole chart (the muraux-by-strate breakdown lost
// two brands and 52 of its 131 units that way).
const dynamicSeries = (rows, exclude) => {
  const excluded = [].concat(exclude);
  const keys = [];
  rows.forEach((row) => Object.keys(row).forEach((key) => {
    if (excluded.includes(key) || key.startsWith('__') || keys.includes(key)) return;
    keys.push(key);
  }));
  return keys;
};

// Series a chart draws: either the keys the data produced ('dynamic'), or an explicit
// list when the block plots several measures side by side.
const seriesOf = (rows, mapping) => (
  mapping.series === 'dynamic'
    ? dynamicSeries(rows, mapping.x)
    : (mapping.series || []).map(s => s.dataKey)
);

// Series label: an explicit series carries its own, a dynamic one IS its label.
const seriesLabeller = (mapping, labels) => {
  if (mapping.series === 'dynamic') return (key) => labels[key] || sanitizeLabel(key);
  const byKey = Object.fromEntries((mapping.series || []).map(s => [s.dataKey, s.label]));
  return (key) => byKey[key] || labels[key] || sanitizeLabel(key);
};

// ── Vertical bars, stacked or grouped ──
// `stacked` comes from the block type: bar_stacked_v stacks its series, bar_grouped_v
// puts them side by side. encoding.showTotals prints the row total above the stack,
// which only reads as a total when the segments are actually stacked.
function VerticalBarRenderer({ data, mapping, config, labels = {}, stacked = true }) {
  const rows = data || [];
  if (!rows.length) return <Empty />;

  const xKey = mapping.x;
  const series = seriesOf(rows, mapping);
  const labelOf = seriesLabeller(mapping, labels);
  const isMonth = config.xFormat === 'month_fr';
  const showTotals = stacked && config.showTotals === true;

  // Derived after the series are resolved, so the total never becomes a series
  // itself (seriesOf also skips __-prefixed keys as a belt-and-braces guard).
  const chartRows = showTotals
    ? rows.map(row => ({ ...row, __total: series.reduce((sum, k) => sum + (Number(row[k]) || 0), 0) }))
    : rows;

  return (
    <>
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={chartRows} margin={{ top: showTotals ? 24 : 10, right: 10, left: 0, bottom: 0 }} barCategoryGap="24%">
          <CartesianGrid strokeDasharray="4 4" stroke="#edeff3" vertical={false} />
          <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 12, fill: '#7c8499' }} tickFormatter={isMonth ? formatMonthFr : undefined} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 11, fill: '#9aa1b1' }} />
          <Tooltip cursor={{ fill: 'rgba(91,61,240,0.05)' }} contentStyle={TOOLTIP_STYLE} formatter={(v, n) => [nf(v), sanitizeLabel(n)]} labelFormatter={isMonth ? formatMonthFr : undefined} />
          {series.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              name={labelOf(k)}
              stackId={stacked ? 'a' : undefined}
              fill={colorFor(k, i)}
              maxBarSize={stacked ? 46 : 22}
              radius={!stacked || i === series.length - 1 ? [5, 5, 0, 0] : 0}
            >
              {showTotals && i === series.length - 1 && (
                <LabelList dataKey="__total" position="top" formatter={nf} style={{ fontSize: 11, fontWeight: 600, fill: '#7c8499' }} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <SeriesLegend series={series} labelOf={labelOf} />
    </>
  );
}

const GroupedBarRenderer = (props) => <VerticalBarRenderer {...props} stacked={false} />;

// Segmented horizontal bar row (shared by cross-tab + strate renderers).
function StackedRow({ label, segments, total, globalMax, labelWidth = 'w-28 sm:w-40' }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`${labelWidth} shrink-0 truncate text-xs font-semibold text-gray-600 dark:text-gray-300`} title={label}>{label}</span>
      <div className="flex-1 h-4 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800">
        <div className="h-full flex" style={{ width: `${globalMax ? (total / globalMax) * 100 : 0}%` }}>
          {segments.map((seg) => seg.value > 0 && (
            <div key={seg.key} style={{ width: `${total ? (seg.value / total) * 100 : 0}%`, background: seg.color }} title={`${seg.key}: ${nf(seg.value)}`} />
          ))}
        </div>
      </div>
      <span className="w-10 shrink-0 text-right text-xs font-bold text-gray-900 dark:text-white tabular-nums">{nf(total)}</span>
    </div>
  );
}

// ── Horizontal stacked bar (cross-tab: label × series) ──
function HorizontalBarRenderer({ data, mapping }) {
  const rows = data || [];
  if (!rows.length) return <Empty />;
  const xKey = mapping.x;
  const series = seriesOf(rows, mapping);
  const withTotal = rows
    .map(r => ({ label: sanitizeLabel(r[xKey]), total: series.reduce((s, k) => s + (Number(r[k]) || 0), 0), row: r }))
    .filter(r => r.total > 0)
    .sort((a, b) => b.total - a.total);
  const globalMax = Math.max(...withTotal.map(r => r.total), 1);
  return (
    <>
      <div className="flex flex-col gap-3">
        {withTotal.map((r, i) => (
          <StackedRow
            key={i}
            label={r.label}
            total={r.total}
            globalMax={globalMax}
            segments={series.map((k, si) => ({ key: sanitizeLabel(k), value: Number(r.row[k]) || 0, color: colorFor(k, si) }))}
          />
        ))}
      </div>
      <SeriesLegend series={series} />
    </>
  );
}

// ── Installations: horizontal bars in labelled groups (muraux by strate × width,
// concepts by concept × format). Rows arrive split and ordered by the query:
// [{ <group>, <row>, <brand>: n, … }]; `unit` names what a group total counts.
function StrateRenderer({ data, mapping }) {
  const rows = data || [];
  if (!rows.length) return <Empty />;
  const { group: groupKey = 'strate', row: rowKey = 'size', unit = 'muraux', wideLabels = false } = mapping;
  // "7H-42 Bacs" needs the wide gutter; mural widths ("130cm") keep the narrow one they had.
  const labelWidth = wideLabels ? 'w-24' : 'w-16';
  const brands = dynamicSeries(rows, [groupKey, rowKey]);
  const groups = new Map();
  let globalMax = 1;
  rows.forEach(r => {
    const group = sanitizeLabel(r[groupKey] ?? 'Autre');
    const total = brands.reduce((s, b) => s + (Number(r[b]) || 0), 0);
    globalMax = Math.max(globalMax, total);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push({ label: sanitizeLabel(r[rowKey] ?? 'Autre'), total, row: r });
  });
  const allSeries = brands.map(sanitizeLabel);
  return (
    <>
      <div className="flex flex-col gap-5">
        {[...groups].map(([g, groupRows]) => {
          const groupTotal = groupRows.reduce((s, r) => s + r.total, 0);
          return (
            <div key={g}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold tracking-wider text-gray-500 dark:text-gray-400 uppercase">{g}</span>
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500">{nf(groupTotal)} {unit}</span>
              </div>
              <div className="flex flex-col gap-2">
                {groupRows.map((r, i) => (
                  <StackedRow
                    key={i}
                    label={r.label}
                    total={r.total}
                    globalMax={globalMax}
                    labelWidth={labelWidth}
                    segments={brands.map((b, bi) => ({ key: sanitizeLabel(b), value: Number(r.row[b]) || 0, color: colorFor(b, bi) }))}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <SeriesLegend series={allSeries} />
    </>
  );
}

// ── Expandable detail list ──
// encoding: {
//   groupKey, groupFormat?,
//   counters: [{ key, label, tone }],          chips on the collapsed row
//   detail: { rowsKey, columns: [{ key, label, badge? }] }
// }
// Rows arrive as [{ <groupKey>, <counter keys…>, <rowsKey>: [ … ] }].
const COUNTER_TONES = {
  alert: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  warn: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  ok: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};
// A column's badge is one tone for every cell, or a { value: tone } map.
const badgeTone = (badge, value) => (typeof badge === 'string' ? badge : (badge || {})[value]) || 'neutral';
const percent = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : null);
const CELL_EMPHASIS = {
  strong: 'font-semibold text-gray-900 dark:text-gray-100',
  muted: 'text-[11.5px] font-semibold text-gray-500 dark:text-gray-400',
};

function DetailListRenderer({ data, mapping }) {
  const [openKey, setOpenKey] = useState(null);
  const rows = data || [];
  if (!rows.length) return <Empty />;

  const { groupKey, primary, secondary, counters = [], detail = {} } = mapping;
  const columns = detail.columns || [];
  const formatGroup = mapping.groupFormat === 'month_fr' ? formatMonthFr : (v) => v;
  const count = (row, key) => Number(row[key]) || 0;
  // Column widths come from the encoding (e.g. "70px", "1.4fr"); a bare fr unit is
  // wrapped in minmax(0, …) so a long store name shrinks instead of overflowing.
  const track = (width) => (!width ? 'minmax(0, 1fr)' : /fr$/.test(width) ? `minmax(0, ${width})` : width);
  const headerGrid = ['64px', primary && 'minmax(0, 1.3fr)', ...counters.map(() => 'minmax(0, 1fr)'), '20px'].filter(Boolean).join(' ');
  const detailGrid = columns.map((column) => track(column.width)).join(' ');
  const cellClass = (column) => [
    column.badge ? 'text-center' : column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : '',
    CELL_EMPHASIS[column.emphasis] || (column.emphasis === 'muted' ? '' : 'text-gray-500 dark:text-gray-400'),
    'min-w-0 truncate',
  ].join(' ');

  return (
    <div className="flex flex-col">
      {rows.map((row) => {
        const key = row[groupKey];
        const open = openKey === key;
        const children = row[detail.rowsKey] || [];

        return (
          <div key={key} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenKey(open ? null : key)}
              style={{ gridTemplateColumns: headerGrid }}
              className="grid w-full items-center gap-2.5 rounded-lg py-[11px] text-left hover:bg-gray-50 dark:hover:bg-gray-900/40"
            >
              <span className="rounded-lg bg-[#f3f1fe] py-1.5 text-center text-xs font-bold text-[#4930c4] dark:bg-[#2a2440] dark:text-[#a98ff5]">
                {formatGroup(key)}
              </span>
              {primary && (
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{nf(count(row, primary.key))} {primary.label}</span>
                  {secondary && (
                    <span className="text-[11.5px] text-gray-400 dark:text-gray-500">{nf(count(row, secondary.key))} {secondary.label}</span>
                  )}
                </span>
              )}
              {counters.map((counter) => {
                const value = count(row, counter.key);
                const ratio = counter.ratioOf ? percent(value, count(row, counter.ratioOf)) : null;
                return (
                  <span key={counter.key} className="flex min-w-0 items-center gap-2">
                    <span className={`min-w-6 rounded-full px-2 py-0.5 text-center text-xs font-bold tabular-nums ${COUNTER_TONES[counter.tone] || COUNTER_TONES.neutral}`}>
                      {nf(value)}
                    </span>
                    <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {counter.label}
                      {ratio ? ` · ${ratio}${counter.ratioLabel ? ` ${counter.ratioLabel}` : ''}` : ''}
                    </span>
                  </span>
                );
              })}
              <span className={`text-center text-[11px] text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden>
                ▾
              </span>
            </button>

            {open && (
              children.length === 0 ? (
                <p className="pb-3 pl-[74px] text-xs text-gray-400">Aucun détail disponible</p>
              ) : (
                <div className="flex flex-col gap-1.5 pb-3.5 pt-1 sm:pl-[74px]">
                  {detail.title && (
                    <p className="mb-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{detail.title}</p>
                  )}
                  <div className="overflow-x-auto">
                    <div className="flex min-w-[560px] flex-col gap-1.5">
                      <div
                        style={{ gridTemplateColumns: detailGrid }}
                        className="grid items-center gap-2.5 px-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                      >
                        {columns.map((column) => (
                          <span key={column.key} className={column.badge || column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : ''}>{column.label}</span>
                        ))}
                      </div>
                      {children.map((child, index) => (
                        <div
                          key={index}
                          style={{ gridTemplateColumns: detailGrid }}
                          className="grid items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-[7px] text-[12.5px] dark:border-gray-800 dark:bg-gray-900/40"
                        >
                          {columns.map((column) => (
                            <span key={column.key} className={cellClass(column)} title={column.badge ? undefined : String(sanitizeLabel(child[column.key]))}>
                              {column.badge ? (
                                <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${COUNTER_TONES[badgeTone(column.badge, child[column.key])]}`}>
                                  {sanitizeLabel(child[column.key])}
                                </span>
                              ) : sanitizeLabel(child[column.key])}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Table ──
// encoding: { firstColumn: { key, label }, columns: string[] | 'dynamic', … }
// Column headers come from `labels` (the measure key -> label dictionary), so the
// wording lives in the registry and never in the stored config.
function TableRenderer({ data, mapping, config, labels = {}, natures = {}, endDate }) {
  if (!data || data.length === 0) return <Empty />;

  const asNumber = (v) => (v != null ? Number(v).toLocaleString('fr-FR') : '-');
  const first = mapping.firstColumn;
  const rowKeys = Object.keys(data[0]);

  // 'dynamic' means "whatever the pivot produced": everything but the row dimension.
  const measureKeys = mapping.columns === 'dynamic' || !mapping.columns
    ? rowKeys.filter((key) => key !== first?.key)
    : mapping.columns;

  const columns = [
    ...(first ? [{ key: first.key, label: first.label || first.key }] : []),
    ...measureKeys.map((key) => ({
      key,
      label: labels[key] || key,
      render: (row) => asNumber(row[key]),
    })),
  ];

  return (
    <>
      <SortableTable
        data={data}
        columns={columns}
        exportable={config.exportable ?? false}
        withSum={config.withSum ?? false}
        globalSearch={config.globalSearch ?? false}
        hideCountLabel
      />
      <NatureLegend natures={natures} labels={labels} endDate={endDate} />
    </>
  );
}

// ── Skeletons ──
function ChartSkeleton() {
  return <div className="animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg h-72" />;
}
function KpiSkeleton({ block }) {
  return (
    <KpiGroupCard block={block}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[...Array(4)].map((_, i) => <div key={i} className="h-[92px] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />)}
      </div>
    </KpiGroupCard>
  );
}

// A block's `type` names its renderer outright — no decoding of display/layout
// flags. The keys here must cover BLOCK_TYPES in backend/customDashboard/pageSchema.js.
const RENDERERS = {
  donut: { R: DonutRenderer, icon: ICONS.pie },
  bar_ranked: { R: MeterRenderer, icon: ICONS.list },
  bar_stacked_v: { R: VerticalBarRenderer, icon: ICONS.bar },
  bar_grouped_v: { R: GroupedBarRenderer, icon: ICONS.bar },
  bar_stacked_h: { R: HorizontalBarRenderer, icon: ICONS.list },
  bar_strate: { R: StrateRenderer, icon: ICONS.list },
  table: { R: TableRenderer, icon: ICONS.table },
  detail_list: { R: DetailListRenderer, icon: ICONS.list },
};

function resolveRenderer(block) {
  return RENDERERS[block.type] || { R: null, icon: ICONS.bar };
}

// ── Custom Block ──
function CustomBlock({ block, projectId, pageId, startDate, endDate, globalFilters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const filtersKey = JSON.stringify(globalFilters);

  useEffect(() => {
    setLoading(true);
    setError(false);

    // Local-time format (matches GPSContext) — toISOString() would shift to UTC
    // and leak a day into the previous year at year boundaries (e.g. 2026 → 2025-12-31).
    const formatDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

    // The block is executed by id: the browser sends filters and a window, never a
    // query spec. The server reads the definition from its own config.
    baseFetchWithAuth(`/api/dwh/gps/custom-page/${encodeURIComponent(projectId)}/${encodeURIComponent(pageId)}/block/${encodeURIComponent(block.id)}`, {
      method: 'POST',
      body: JSON.stringify({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        globalFilters,
      }),
    })
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, projectId, pageId, startDate, endDate, filtersKey]);

  const clean = sanitizeData(data);
  const encoding = block.encoding || {};
  // Labels et natures viennent du registre de mesures pour un bloc déclaratif ;
  // une requête nommée n'en a pas, son bloc les déclare alors dans l'encodage.
  const labels = { ...(block.measureLabels || {}), ...(encoding.labels || {}) };
  const natures = { ...(block.measureNatures || {}), ...(encoding.natures || {}) };

  // KPI groups render as a labelled section of tiles (no card chrome).
  if (block.type === 'kpi_group') {
    if (loading) return <KpiSkeleton block={block} />;
    if (error) {
      return (
        <KpiGroupCard block={block}>
          <p className="text-sm text-red-500">Erreur de chargement</p>
        </KpiGroupCard>
      );
    }
    return <KpiSection block={block} data={clean} labels={labels} natures={natures} endDate={endDate} />;
  }

  const { R, icon } = resolveRenderer(block);

  return (
    <BlockCard icon={icon} title={block.title} subtitle={block.subtitle} details={block.details} meta={encoding.meta} slug={block.id}>
      {loading ? (
        <ChartSkeleton />
      ) : error ? (
        <p className="text-sm text-red-500 text-center py-10">Erreur de chargement</p>
      ) : R ? (
        <R data={clean} mapping={encoding} config={encoding} labels={labels} natures={natures} endDate={endDate} />
      ) : (
        <p className="text-sm text-gray-400 text-center py-10">Type de bloc non supporté : {block.type}</p>
      )}
    </BlockCard>
  );
}

export { splitSubtitle, sanitizeData, NatureLegend, NatureBadge, KpiSection, VerticalBarRenderer, GroupedBarRenderer, DetailListRenderer, TableRenderer, DonutRenderer, MeterRenderer, StrateRenderer };
export default CustomBlock;
